import posthog from 'posthog-js'
import { analyticsLoaded } from '@/lib/analytics/analytics-ready'
import { trackEvent } from '@/lib/analytics'

/**
 * Delegated tracking for the internal-link modules on blog, guide, listing,
 * tool, weed and pest pages (WP-D of the 2026-09-13 content CTA audit).
 *
 * Every module marks its container with `data-module="<name>"`. A single
 * document-level click listener resolves the nearest such ancestor of the
 * clicked anchor and emits ONE `content_link_clicked`. Nothing here renames or
 * replaces an existing event; modules that already emit their own click event
 * opt out with `data-content-link-skip`.
 */

export const CONTENT_LINK_MODULES = [
  'free_tools_grid',
  'read_full_guide',
  'related_articles',
  'author',
  'prev_next',
  'see_all_products',
  'guide_tool_callout',
  'guide_quick_start',
  'guide_sidebar_tool',
  'pillar_compare',
  'cross_link',
  'city_grid',
  'listing_card',
  'category_card',
  'author_card',
  'species_strip',
  'guides_landing_card',
  'guides_landing_strip',
  'faq_expand',
  'tool_related_articles',
  'tool_pillar_callout',
  'weed_id_inline',
  'diagnose_closing',
  'city_grass_card',
  'city_article',
  'city_next_step',
] as const

export type ContentLinkModule = (typeof CONTENT_LINK_MODULES)[number]

export const CONTENT_MODULE_ATTR = 'data-module'
/**
 * Marks one list among several on a page (the guides landing grids). Clicks
 * inside it gain `list_id` and a 1-based `list_position`; `position` keeps its
 * module-relative meaning.
 */
export const CONTENT_LIST_ID_ATTR = 'data-list-id'
/** Opt-out marker for anchors already covered by a dedicated click event. */
export const CONTENT_LINK_SKIP_ATTR = 'data-content-link-skip'

const MAX_TEXT_LENGTH = 120

export type PageFamily =
  | 'blog_post'
  | 'blog_listing'
  | 'blog_category'
  | 'blog_author'
  | 'guide_post'
  | 'guides_landing'
  | 'tools_hub'
  | 'tool_page'
  | 'weeds_landing'
  | 'weed_guide'
  | 'pests_landing'
  | 'pest_guide'
  | 'lawn_care_hub'
  | 'lawn_care_page'

function segmentsOf(pathname: string): string[] {
  return pathname.split('?')[0].split('#')[0].split('/').filter(Boolean)
}

/** Content family for a route, or null for routes outside the content sections. */
export function pageFamilyFromPathname(pathname?: string | null): PageFamily | null {
  if (!pathname) return null
  const segments = segmentsOf(pathname)
  if (segments[0] === 'blog') {
    if (segments.length === 1) return 'blog_listing'
    if (segments[1] === 'archive') return 'blog_listing'
    if (segments[1] === 'category') return 'blog_category'
    if (segments[1] === 'author') return 'blog_author'
    return 'blog_post'
  }
  if (segments[0] === 'guides') {
    return segments.length === 1 ? 'guides_landing' : 'guide_post'
  }
  if (segments[0] === 'tools') {
    return segments.length === 1 ? 'tools_hub' : 'tool_page'
  }
  if (segments[0] === 'weeds') {
    return segments.length === 1 ? 'weeds_landing' : 'weed_guide'
  }
  if (segments[0] === 'pests') {
    return segments.length === 1 ? 'pests_landing' : 'pest_guide'
  }
  if (segments[0] === 'lawn-care') {
    return segments.length === 1 ? 'lawn_care_hub' : 'lawn_care_page'
  }
  return null
}

/** The post / guide / category / author slug the page is about, when it has one. */
export function contentSlugFromPathname(pathname?: string | null): string | null {
  const family = pageFamilyFromPathname(pathname)
  if (!family || !pathname) return null
  const segments = segmentsOf(pathname)
  switch (family) {
    case 'blog_post':
    case 'guide_post':
    case 'tool_page':
    case 'weed_guide':
    case 'pest_guide':
    case 'lawn_care_page':
      return segments[1] ?? null
    case 'blog_category':
    case 'blog_author':
      return segments[2] ?? null
    default:
      return null
  }
}

/** Route the listener is firing on. Null under SSR. */
function currentPathname(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.location.pathname || '/'
  } catch {
    return null
  }
}

/**
 * Modules whose container is owned by another surface and cannot carry a
 * `data-module` attribute; matched on the anchor's href instead.
 */
const HREF_MODULES: Array<[ContentLinkModule, (href: string) => boolean]> = [
  ['see_all_products', (href) => href.startsWith('/products?source=')],
]

/**
 * Anchors already handled by a dedicated branch in
 * `components/ui/blog-content-with-toc.tsx` (affiliate anchors, auto-linked
 * prose CTAs, the fast-answer diagnose row). They emit their own event, so
 * `content_link_clicked` must not double-count them.
 */
export function isExistingBranchAnchor(anchor: Element): boolean {
  return (
    anchor.hasAttribute('data-affiliate') ||
    anchor.hasAttribute('data-cta-location') ||
    anchor.classList.contains('diagnose-row-cta')
  )
}

export function resolveModuleForAnchor(
  anchor: Element,
): { module: string; root: Element } | null {
  const scoped = anchor.closest(`[${CONTENT_MODULE_ATTR}]`)
  if (scoped) {
    const module = scoped.getAttribute(CONTENT_MODULE_ATTR)?.trim()
    if (module) return { module, root: scoped }
  }
  const href = anchor.getAttribute('href') ?? ''
  for (const [module, test] of HREF_MODULES) {
    if (test(href)) return { module, root: anchor }
  }
  return null
}

function isOptedOut(anchor: Element, root: Element): boolean {
  let node: Element | null = anchor
  while (node) {
    if (node.hasAttribute(CONTENT_LINK_SKIP_ATTR)) return true
    if (node === root) return false
    node = node.parentElement
  }
  return false
}

/** 1-based index of the anchor among the module's anchors. */
export function positionOfAnchor(anchor: Element, root: Element): number {
  if (root === anchor) return 1
  const anchors = Array.from(root.querySelectorAll('a[href]'))
  const index = anchors.indexOf(anchor)
  return index < 0 ? 1 : index + 1
}

export interface ContentLinkClickedProps {
  module: string
  destination: string
  link_text: string
  position: number
  slug: string | null
  page_family: PageFamily | null
  list_id?: string
  list_position?: number
}

export function trackContentLinkClicked(props: ContentLinkClickedProps): void {
  trackEvent('content_link_clicked', { ...props })
}

/**
 * Once-per-event lock. The blog article body runs its own delegated listener on
 * the content element, which bubbles into the document-level one installed by
 * `ContentLinkTracker`; without this the inner and outer handler would both
 * emit for the same click.
 */
const handledEvents = new WeakSet<object>()

export interface ContentLinkClickOptions {
  pathname?: string | null
  /**
   * Set by the blog article listener. `/blog/...` and bare `#slug` anchors in
   * article HTML already emit `internal_link_clicked` from the same handler, so
   * they are left to that branch rather than double-reported.
   */
  skipInternalBlogLinks?: boolean
}

/**
 * Hrefs the blog article listener routes through its own internal-link branch.
 * A `#slug` anchor only counts when nothing on the page owns that id: the
 * article listener rewrites those to `/blog/<slug>` and reports them as
 * internal links, while a resolving `#id` is an ordinary in-page jump (the
 * species strip) that no other branch reports.
 */
function isInternalBlogBranchHref(href: string): boolean {
  if (href.startsWith('/blog/')) return true
  const match = /^#([a-z0-9-]+)$/.exec(href)
  if (!match) return false
  if (typeof document === 'undefined') return true
  return document.getElementById(match[1]) === null
}

/** Returns true when a `content_link_clicked` was emitted for this event. */
export function handleContentLinkClick(
  event: Event,
  options: ContentLinkClickOptions = {},
): boolean {
  if (handledEvents.has(event)) return false
  const target = event.target as Element | null
  if (!target || typeof target.closest !== 'function') return false
  const anchor = target.closest('a')
  if (!anchor) return false
  if (isExistingBranchAnchor(anchor)) return false

  const href = anchor.getAttribute('href')
  if (!href) return false
  if (options.skipInternalBlogLinks && isInternalBlogBranchHref(href)) return false

  const resolved = resolveModuleForAnchor(anchor)
  if (!resolved) return false
  if (isOptedOut(anchor, resolved.root)) return false

  handledEvents.add(event)
  const pathname = options.pathname ?? currentPathname()
  const list = anchor.closest(`[${CONTENT_LIST_ID_ATTR}]`)
  const listId = list?.getAttribute(CONTENT_LIST_ID_ATTR)?.trim()
  trackContentLinkClicked({
    module: resolved.module,
    destination: href,
    link_text: (anchor.textContent ?? '').trim().slice(0, MAX_TEXT_LENGTH),
    position: positionOfAnchor(anchor, resolved.root),
    slug: contentSlugFromPathname(pathname),
    page_family: pageFamilyFromPathname(pathname),
    ...(list && listId ? { list_id: listId, list_position: positionOfAnchor(anchor, list) } : {}),
  })
  return true
}

/**
 * Install the delegated listener on `document`. Bubble phase on purpose: the
 * blog article body's own listener sits on an inner element and must get first
 * refusal so its affiliate / prose / internal-link branches keep priority.
 */
export function installContentLinkListener(
  getPathname: () => string | null,
): () => void {
  if (typeof document === 'undefined') return () => {}
  const onClick = (event: Event) => {
    handleContentLinkClick(event, { pathname: getPathname() })
  }
  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}

/**
 * FAQ accordion expand. Not an anchor click, so it calls in directly; only the
 * open transition counts, and only on a content route.
 */
export function trackFaqExpand(args: {
  index: number
  question?: string
  id?: string
  pathname?: string | null
}): boolean {
  const pathname = args.pathname ?? currentPathname()
  const pageFamily = pageFamilyFromPathname(pathname)
  if (!pageFamily) return false
  trackContentLinkClicked({
    module: 'faq_expand',
    destination: args.id ?? `faq_${args.index}`,
    link_text: (args.question ?? '').trim().slice(0, MAX_TEXT_LENGTH),
    position: args.index + 1,
    slug: contentSlugFromPathname(pathname),
    page_family: pageFamily,
  })
  return true
}

// --- page_family super property -------------------------------------------

let registeredPageFamily: PageFamily | null = null

/**
 * Returns true once the value is actually held by posthog-js. `register` and
 * `unregister` are silent no-ops before `posthog.init()` (no persistence yet),
 * so the module guard is only advanced when the SDK is loaded; a caller that
 * sees `false` must retry after init (see PageFamilyTracker).
 */
export function registerPageFamily(family: PageFamily | null): boolean {
  if (family === registeredPageFamily) return true
  if (!analyticsLoaded()) return false
  registeredPageFamily = family
  try {
    if (family) posthog.register({ page_family: family })
    else posthog.unregister('page_family')
  } catch {
    /* SSR shim or blocked script */
  }
  return true
}

/**
 * Release the property only if it is still the one this caller registered.
 * Blog -> guides navigation renders the incoming layout before the outgoing
 * layout's cleanup runs, so an unconditional unregister would drop the value
 * the new route just set.
 */
export function clearPageFamily(family: PageFamily | null): void {
  if (registeredPageFamily !== family) return
  registeredPageFamily = null
  try {
    posthog.unregister('page_family')
  } catch {
    /* PostHog not initialised */
  }
}

export function currentRegisteredPageFamily(): PageFamily | null {
  return registeredPageFamily
}

/** Test seam. */
export function resetPageFamilyForTests(): void {
  registeredPageFamily = null
}
